import React, { useState, useEffect, useCallback } from "react";
import { T, load, save } from "./theme";
import { Btn, Avatar } from "./ui";
import { Icon } from "./Icons";
import {
  MODULES, DEFAULT_USERS, DEFAULT_CORRETORES, TIMES,
  METAS_DEFAULT, ACELERADORES_DEFAULT, PREMIOS_DEFAULT, METAS_INDIVIDUAIS_DEFAULT,
  DEFAULT_CLIENTES, DEFAULT_PEDIDOS, DEFAULT_TICKETS,
} from "./data";
import { isSupabaseReady } from "./supabase";
import {
  fetchCorretores as sbFetchCorretores,
  fetchTimes as sbFetchTimes,
  fetchConfig,
  saveConfig,
} from "./supabaseData";

import Login       from "./Login";
import CreateAccount from "./CreateAccount";
import LandingPage from "./LandingPage";
import OfertasCopa from "./OfertasCopa";
import Regulamento from "./Regulamento";
import Ranking     from "./Ranking";
import CorretoresHub from "./CorretoresHub";
import Times       from "./Times";
import Regional    from "./Regional";
import Nacional from "./Nacional";
import ClientesHub from "./ClientesHub";
import Vendas      from "./Vendas";
import Propostas   from "./Propostas";
import Financeiro  from "./Financeiro";
import ConfigCorretor from "./ConfigCorretor";
import RedeRelacionamento from "./RedeRelacionamento";
import Configuracoes from "./Configuracoes";
import Dashboard   from "./Dashboard";
import DashboardAdmin from "./DashboardAdmin";
import PlantaoAdmin from "./PlantaoAdmin";
import PlantaoCorretor from "./PlantaoCorretor";

// ═══════════════════════════════════════════════════════════════════════
// APP ROOT — Bellanor Alta Performance
// Fonte primária: Supabase · Fallback: localStorage
// ═══════════════════════════════════════════════════════════════════════

const NAV_ICONS = {
  dashboard: Icon.home, vendas: Icon.client,
  ranking: Icon.trophy, corretores: Icon.users,
  financeiro: Icon.chart, times: Icon.team,
  regional: Icon.map, nacional: Icon.globe,
  clientes: Icon.client, rede: Icon.team,
  plantao: Icon.rocket,
  meusettings: Icon.settings, config: Icon.settings,
  propostas: Icon.client,
};

// Subitens do grupo "Vendas" na sidebar (estilo Wix > Pedidos)
const GRUPO_VENDAS = ["vendas", "propostas", "plantao"];
const SUB_LABELS = { vendas: "Pedidos", propostas: "Propostas", plantao: "Plantão" };

// Navegação da sidebar com o grupo "Vendas" (Pedidos + Plantão)
function SidebarNav({ items, modulo, onSelect }) {
  // Só monta o grupo "Vendas" quando o usuário tem o módulo Pedidos (admin).
  // Corretor (só tem Plantão) vê o item solto, sem grupo.
  const temPedidos = items.some(m => m.id === "vendas");
  const children = temPedidos ? items.filter(m => GRUPO_VENDAS.includes(m.id)) : [];
  const childActive = children.some(m => m.id === modulo);
  const [aberto, setAberto] = useState(true);
  const NavBtn = (mod, indent) => {
    const IconComp = NAV_ICONS[mod.id] || Icon.settings;
    const ativo = modulo === mod.id;
    return (
      <button key={mod.id} onClick={() => onSelect(mod.id)} style={{
        background: ativo ? T.brandSoft : "transparent",
        color: ativo ? T.brand : T.textSec,
        border: "none", borderRadius: T.r.md,
        padding: indent ? "9px 12px 9px 32px" : "10px 12px",
        cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
        fontSize: 13, fontWeight: ativo ? 600 : 400,
        fontFamily: "inherit", textAlign: "left", width: "100%",
        borderLeft: `2px solid ${ativo ? T.brand : "transparent"}`,
      }}>
        <IconComp size={14} />
        <span>{SUB_LABELS[mod.id] || mod.label}</span>
      </button>
    );
  };
  const out = [];
  let grupoDone = false;
  items.forEach(mod => {
    if (temPedidos && GRUPO_VENDAS.includes(mod.id)) {
      if (grupoDone || !children.length) return;
      grupoDone = true;
      const GIcon = NAV_ICONS.vendas || Icon.client;
      out.push(
        <div key="grupo-vendas" style={{ display: "flex", flexDirection: "column" }}>
          <button onClick={() => setAberto(a => !a)} style={{
            background: (childActive && !aberto) ? T.brandSoft : "transparent",
            color: childActive ? T.brand : T.textSec,
            border: "none", borderRadius: T.r.md, padding: "10px 12px",
            cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
            fontSize: 13, fontWeight: 600, fontFamily: "inherit", textAlign: "left", width: "100%",
          }}>
            <GIcon size={14} />
            <span style={{ flex: 1 }}>Vendas</span>
            <span style={{ fontSize: 12, display: "inline-block", transform: aberto ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
          </button>
          {aberto && children.map(c => NavBtn(c, true))}
        </div>
      );
      return;
    }
    out.push(NavBtn(mod, false));
  });
  return out;
}

export default function App() {
  // ─── Session ───────────────────────────────────────────────────────
  const [user, setUser] = useState(() => load("bap_session", null));
  const [modulo, setModulo] = useState("ranking");
  const [menuMobile, setMenuMobile] = useState(false);

  // ─── Loading & data source ────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState("local"); // "supabase" ou "local"

  // ─── Data state (inicializa com localStorage, depois Supabase sobrescreve)
  const [users, setUsers] = useState(() => load("bap_users", DEFAULT_USERS));
  const [corretores, setCorretores] = useState(() => load("bap_corretores", DEFAULT_CORRETORES));
  const [times, setTimes] = useState(() => load("bap_times", TIMES));
  const [metas, setMetas] = useState(() => load("bap_metas", METAS_DEFAULT));
  const [aceleradores, setAceleradores] = useState(() => load("bap_aceleradores", ACELERADORES_DEFAULT));
  const [premios, setPremios] = useState(() => load("bap_premios", PREMIOS_DEFAULT));
  const [metasIndividuais, setMetasIndividuais] = useState(() => load("bap_metas_individuais", METAS_INDIVIDUAIS_DEFAULT));
  const [clientes, setClientes] = useState(() => load("bap_clientes", DEFAULT_CLIENTES));
  const [pedidos, setPedidos] = useState(() => load("bap_pedidos", DEFAULT_PEDIDOS));
  const [tickets, setTickets] = useState(() => load("bap_tickets", DEFAULT_TICKETS));

  // ─── Busca dados do Supabase ao montar ────────────────────────────
  const loadFromSupabase = useCallback(async () => {
    if (!isSupabaseReady) {
      console.log("[Bellanor] Supabase não configurado — usando localStorage");
      setLoading(false);
      return;
    }

    try {
      console.log("[Bellanor] Buscando dados do Supabase...");
      const [sbCorretores, sbTimes, sbMetas, sbAceleradores, sbPremios, sbMetasInd] =
        await Promise.all([
          sbFetchCorretores(),
          sbFetchTimes(),
          fetchConfig("metas"),
          fetchConfig("aceleradores"),
          fetchConfig("premios"),
          fetchConfig("metas_individuais"),
        ]);

      let source = "local";

      // Corretores e times — prioridade máxima
      if (sbCorretores?.length) {
        setCorretores(sbCorretores);
        save("bap_corretores", sbCorretores);
        source = "supabase";
        console.log(`[Bellanor] ✓ ${sbCorretores.length} corretores carregados do Supabase`);
      }
      if (sbTimes?.length) {
        setTimes(sbTimes);
        save("bap_times", sbTimes);
        source = "supabase";
        console.log(`[Bellanor] ✓ ${sbTimes.length} times carregados do Supabase`);
      }

      // Configurações (usa Supabase se existir, senão mantém defaults)
      if (sbMetas) { setMetas(sbMetas); save("bap_metas", sbMetas); }
      if (sbAceleradores) { setAceleradores(sbAceleradores); save("bap_aceleradores", sbAceleradores); }
      if (sbPremios) { setPremios(sbPremios); save("bap_premios", sbPremios); }
      if (sbMetasInd) { setMetasIndividuais(sbMetasInd); save("bap_metas_individuais", sbMetasInd); }

      setDataSource(source);
    } catch (err) {
      console.warn("[Bellanor] Falha ao buscar Supabase, usando cache local:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFromSupabase(); }, [loadFromSupabase]);

  // ─── Resolve corretorId por UUID depois que corretores carregam ────
  useEffect(() => {
    if (user?._corretorUuid && corretores.length > 0) {
      const match = corretores.find(c => c._uuid === user._corretorUuid);
      if (match && match.id !== user.corretorId) {
        const fixed = { ...user, corretorId: match.id };
        setUser(fixed);
        save("bap_session", fixed);
        console.log(`[Bellanor] ✓ corretorId resolvido: ${match.nome} (${match.id})`);
      }
    }
  }, [corretores, user?._corretorUuid]);

  // ─── Persistência: salva no localStorage sempre (cache offline) ──
  // Configs também salvam no Supabase quando mudam
  useEffect(() => save("bap_users", users), [users]);
  useEffect(() => save("bap_corretores", corretores), [corretores]);
  useEffect(() => save("bap_times", times), [times]);
  useEffect(() => {
    save("bap_metas", metas);
    if (dataSource === "supabase") saveConfig("metas", metas);
  }, [metas, dataSource]);
  useEffect(() => {
    save("bap_aceleradores", aceleradores);
    if (dataSource === "supabase") saveConfig("aceleradores", aceleradores);
  }, [aceleradores, dataSource]);
  useEffect(() => {
    save("bap_premios", premios);
    if (dataSource === "supabase") saveConfig("premios", premios);
  }, [premios, dataSource]);
  useEffect(() => {
    save("bap_metas_individuais", metasIndividuais);
    if (dataSource === "supabase") saveConfig("metas_individuais", metasIndividuais);
  }, [metasIndividuais, dataSource]);
  useEffect(() => save("bap_clientes", clientes), [clientes]);
  useEffect(() => save("bap_pedidos", pedidos), [pedidos]);
  useEffect(() => save("bap_tickets", tickets), [tickets]);
  useEffect(() => { if (user) save("bap_session", user); }, [user]);

  // Define módulo inicial baseado no role
  useEffect(() => {
    if (user) {
      const primeiro = user.modules[0] || "ranking";
      setModulo(primeiro);
    }
  }, [user?.id]);

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: T.gradient.bgRadial,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 24,
      }}>
        <img src="./logo-bellanor.png" alt="" style={{ width: 64, height: 64, opacity: 0.8 }}
          onError={e => { e.target.style.display = "none"; }} />
        <div style={{ color: T.gold, fontSize: 13, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>
          Carregando dados...
        </div>
        <div style={{
          width: 120, height: 2, background: T.elev, borderRadius: 1, overflow: "hidden",
        }}>
          <div style={{
            width: "40%", height: "100%", background: T.brand, borderRadius: 1,
            animation: "loadBar 1.2s ease-in-out infinite",
          }} />
        </div>
        <style>{`
          @keyframes loadBar {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(150%); }
            100% { transform: translateX(-100%); }
          }
        `}</style>
      </div>
    );
  }

  // Rotas públicas (acessíveis sem login)
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    if (path.startsWith("/create-account")) return <CreateAccount />;
    if (path.startsWith("/altaperformance") || path.startsWith("/landing")) return <LandingPage />;
    if (path.startsWith("/ofertasdacopa2026") || path.startsWith("/copa2026") || path.startsWith("/bellezabrasileira")) return <OfertasCopa />;
    if (path.startsWith("/regulamento") || path.startsWith("/termos")) {
      return (
        <div style={{ minHeight: "100vh", padding: "20px", maxWidth: 900, margin: "0 auto" }}>
          <Regulamento />
        </div>
      );
    }
  }

  if (!user) return <Login onLogin={setUser} users={users} dataSource={dataSource} />;

  // Força re-login se sessão antiga não tem UUID (fix de mapeamento)
  if (user.role === "corretor" && user.corretorId && !user._corretorUuid) {
    localStorage.removeItem("bap_session");
    setUser(null);
    return <Login onLogin={setUser} users={users} dataSource={dataSource} />;
  }

  // Módulos visíveis para esse usuário
  const ADMIN_ONLY    = ["vendas", "propostas", "corretores", "clientes", "config"];
  const CORRETOR_ONLY = ["meusettings"];
  const isComercial = user.role === "comercial";
  const isAdmin = user.role === "admin" || user.role === "admin_master" || user.role === "proprietario" || isComercial;
  // Comercial tem acesso de admin MAS também é corretor → mantém os módulos de corretor (meusettings)
  const escondeCorretor = isAdmin && !isComercial;
  const modulosVisiveis = MODULES.filter(m =>
    (isAdmin || user.modules.includes(m.id))
    && (isAdmin || !ADMIN_ONLY.includes(m.id))
    && (!escondeCorretor || !CORRETOR_ONLY.includes(m.id))
  );

  // Bottom nav mostra os 4 primeiros módulos do usuário + botão "Mais"
  // Os mais importantes para o usuário padrão (ranking, times, regional, nacional)
  const MOBILE_PRIORITIES = ["dashboard", "ranking", "times", "regional", "nacional", "clientes", "logistica", "suporte", "corretores"];
  const bottomNavItems = [...modulosVisiveis]
    .sort((a, b) => {
      const ia = MOBILE_PRIORITIES.indexOf(a.id);
      const ib = MOBILE_PRIORITIES.indexOf(b.id);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .slice(0, 4)
    .map(m => ({
      ...m,
      shortLabel: {
        dashboard: "Dashboard",
        ranking: "Ranking",
        corretores: "Corretores",
        times: "Times",
        regional: "Regional",
        nacional: "Nacional",
        clientes: "Clientes",
        logistica: "Operações",
        suporte: "Suporte",
        timesAdmin: "Times",
        simulador: "Simulador",
        ia: "IA",
        premios: "Prêmios",
        aceleradores: "Metas",
        usuarios: "Usuários",
        regulamento: "Regras",
      }[m.id] || m.label.split(" ")[0],
    }));

  // ─── Render do módulo atual ───────────────────────────────────────
  // corretoresPub = oculta proprietário/admin (usado em módulos do corretor)
  const corretoresPub = corretores.filter(c => c.tipo !== "proprietario" && c.tipo !== "admin");

  function renderModulo() {
    switch (modulo) {
      case "dashboard":
        return (user.role === "admin" || user.role === "admin_master" || user.role === "proprietario" || user.role === "comercial")
          ? <DashboardAdmin currentUser={user} corretores={corretores} metas={metas} aceleradores={aceleradores} premios={premios} times={times} metasIndividuais={metasIndividuais} onNavigate={setModulo} dataSource={dataSource} />
          : <Dashboard currentUser={user} corretores={corretoresPub} metas={metas} aceleradores={aceleradores} premios={premios} times={times} metasIndividuais={metasIndividuais} onNavigate={setModulo} />;
      case "vendas":
        return <Vendas corretores={corretores} times={times} currentUser={user} />;
      case "propostas":
        return <Propostas corretores={corretores} currentUser={user} />;
      case "ranking":
        return <Ranking corretores={isAdmin ? corretores : corretoresPub} currentUser={user} />;
      case "corretores":
        return <CorretoresHub corretores={corretores} setCorretores={setCorretores} times={times} currentUser={user} setUser={setUser} />;
      case "financeiro":
        return <Financeiro corretores={corretores} currentUser={user} />;
      case "times":
        return <Times corretores={isAdmin ? corretores : corretoresPub} metas={metas} times={times} />;
      case "regional":
        return <Regional corretores={isAdmin ? corretores : corretoresPub} metas={metas} />;
      case "nacional":
        return <Nacional corretores={isAdmin ? corretores : corretoresPub} metas={metas} />;
      case "clientes":
        return <ClientesHub clientes={clientes} setClientes={setClientes} corretores={corretores} pedidos={pedidos} setPedidos={setPedidos} tickets={tickets} setTickets={setTickets} />;
      case "rede":
        return <RedeRelacionamento corretores={isAdmin ? corretores : corretoresPub} setCorretores={setCorretores} times={times} currentUser={user} />;
      case "meusettings":
        return <ConfigCorretor currentUser={user} corretores={corretores} times={times} setUser={setUser} />;
      case "config":
        return <Configuracoes currentUser={user} times={times} setTimes={setTimes} corretores={corretores} setCorretores={setCorretores} metasIndividuais={metasIndividuais} setMetasIndividuais={setMetasIndividuais} premios={premios} setPremios={setPremios} aceleradores={aceleradores} setAceleradores={setAceleradores} metas={metas} setMetas={setMetas} users={users} setUsers={setUsers} />;
      case "plantao":
        return isAdmin
          ? <PlantaoAdmin currentUser={user} corretores={corretores} />
          : <PlantaoCorretor currentUser={user} />;
      default:
        return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Módulo não disponível.</div>;
    }
  }

  const userInitials = user.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, display: "flex", flexDirection: "column" }}>

      {/* ═══ HEADER ═══ */}
      <header style={{
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        padding: "14px 24px", position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        backdropFilter: "blur(8px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={() => setMenuMobile(true)}
            style={{
              background: "none", border: `1px solid ${T.border}`, borderRadius: T.r.md,
              padding: 8, cursor: "pointer", color: T.text,
              display: "none",
            }}
            className="menu-mobile-btn"
          >
            <Icon.settings size={16} />
          </button>
          <div className="header-brand-group" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="./logo-bellanor.png" alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
            <div className="serif" style={{
              fontSize: 22, fontWeight: 600, color: T.brand,
              letterSpacing: 0.5, lineHeight: 1, textTransform: "uppercase",
            }}>BELLANOR</div>
            <div className="ap-sep" style={{ width: 1, height: 18, background: T.borderMid, opacity: 0.6 }} />
            <div style={{
              fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
              color: T.gold, fontWeight: 700, whiteSpace: "nowrap",
            }} className="ap-label">Alta Performance</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ textAlign: "right", display: "none" }} className="user-info-lg">
            <div style={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{user.nome}</div>
            <div style={{ fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5 }}>{user.role}</div>
          </div>
          <Avatar initials={userInitials} size={34} tone={T.brand} />
          <Btn variant="ghost" icon={<Icon.logout size={13} />} onClick={() => { save("bap_session", null); localStorage.removeItem("bap_session"); setUser(null); }}>
            <span style={{ display: "none" }} className="logout-label-lg">Sair</span>
          </Btn>
        </div>

        <style>{`
          @media (min-width: 900px) {
            .user-info-lg { display: block !important; }
            .logout-label-lg { display: inline !important; }
          }
          @media (max-width: 899px) {
            .menu-mobile-btn { display: flex !important; }
            .sidebar-desktop { display: none !important; }
            .ap-label { font-size: 6.5px !important; letter-spacing: 0.8px !important; }
          }
          @media (max-width: 420px) {
            .header-brand-group { gap: 6px !important; }
            .header-brand-group .serif { font-size: 17px !important; }
            .header-brand-group img { width: 22px !important; height: 22px !important; }
            .header-brand-group .ap-sep { height: 14px !important; }
          }
        `}</style>
      </header>

      {/* ═══ LAYOUT ═══ */}
      <div style={{ display: "flex", flex: 1 }}>

        {/* SIDEBAR desktop */}
        <aside className="sidebar-desktop" style={{
          width: 240, background: T.surface,
          borderRight: `1px solid ${T.border}`,
          padding: 14, display: "flex", flexDirection: "column", gap: 4,
          minHeight: "calc(100vh - 62px)",
          position: "sticky", top: 62, alignSelf: "flex-start",
        }}>
          <SidebarNav items={modulosVisiveis} modulo={modulo} onSelect={setModulo} />

          <div style={{ marginTop: "auto", padding: "12px 10px", fontSize: 9, color: T.textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>
            v5.0 · app.bellanor.com.br
            {isAdmin && (
              <div style={{
                marginTop: 4, fontSize: 8, color: dataSource === "supabase" ? T.ok : T.warn,
                display: "flex", alignItems: "center", gap: 4,
              }}>
                <span style={{
                  width: 5, height: 5, borderRadius: "50%",
                  background: dataSource === "supabase" ? T.ok : T.warn,
                  display: "inline-block",
                }} />
                {dataSource === "supabase" ? "Supabase ativo" : "Dados locais"}
              </div>
            )}
          </div>
        </aside>

        {/* SIDEBAR mobile (overlay) */}
        {menuMobile && (
          <div onClick={() => setMenuMobile(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 150, display: "flex",
          }}>
            <aside onClick={e => e.stopPropagation()} style={{
              width: 260, background: T.surface, padding: 16,
              display: "flex", flexDirection: "column", gap: 4,
              overflowY: "auto",
              animation: "slideInLeft 240ms ease",
            }}>
              <style>{`@keyframes slideInLeft { from { transform: translateX(-100%) } to { transform: none } }`}</style>
              <div style={{ marginBottom: 8, padding: "0 10px", display: "flex", alignItems: "center", gap: 8 }}>
                <img src="./logo-bellanor.png" alt="" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
                <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.brand, textTransform: "uppercase", letterSpacing: 0.5 }}>BELLANOR</div>
                <div style={{ width: 1, height: 14, background: T.borderMid, opacity: 0.6 }} />
                <div style={{ fontSize: 7, letterSpacing: 1.2, textTransform: "uppercase", color: T.gold, fontWeight: 700 }}>Alta Performance</div>
              </div>
              <SidebarNav items={modulosVisiveis} modulo={modulo} onSelect={(id) => { setModulo(id); setMenuMobile(false); }} />
            </aside>
          </div>
        )}

        {/* MAIN */}
        <main style={{
          flex: 1,
          padding: "24px 28px",
          paddingBottom: "100px",  // espaço para bottom nav mobile
          maxWidth: 1200,
          margin: "0 auto",
          width: "100%",
        }}
          className="main-content"
        >
          {renderModulo()}
        </main>

        <style>{`
          @media (max-width: 640px) {
            .main-content { padding: 16px 14px 100px !important; }
          }
        `}</style>
      </div>

      {/* ═══ BOTTOM NAV (mobile only) ═══ */}
      <nav className="bottom-nav" style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: T.surface,
        borderTop: `1px solid ${T.borderMid}`,
        display: "none",
        justifyContent: "space-around",
        alignItems: "stretch",
        padding: "8px 4px calc(8px + env(safe-area-inset-bottom))",
        zIndex: 90,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.4)",
      }}>
        {bottomNavItems.map(mod => {
          const IconComp = NAV_ICONS[mod.id] || Icon.settings;
          const ativo = modulo === mod.id;
          return (
            <button
              key={mod.id}
              onClick={() => setModulo(mod.id)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: ativo ? T.brand : T.textSub,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                padding: "6px 8px",
                flex: 1, minWidth: 0,
                fontFamily: "inherit",
                transition: `color 160ms ${T.ease}`,
                position: "relative",
              }}
            >
              {ativo && (
                <div style={{
                  position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
                  width: 24, height: 2, background: T.brand, borderRadius: 1,
                }} />
              )}
              <IconComp size={20} />
              <span style={{
                fontSize: 10, fontWeight: ativo ? 600 : 500,
                letterSpacing: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                maxWidth: "100%",
              }}>{mod.shortLabel || mod.label.split(" ")[0]}</span>
            </button>
          );
        })}
        {/* Botão "Mais" que abre o menu lateral com os outros módulos */}
        {modulosVisiveis.length > 5 && (
          <button
            onClick={() => setMenuMobile(true)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.textSub,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              padding: "6px 8px",
              flex: 1, minWidth: 0,
              fontFamily: "inherit",
            }}
          >
            <Icon.settings size={20} />
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: 0.2 }}>Mais</span>
          </button>
        )}
      </nav>

      <style>{`
        @media (max-width: 899px) {
          .bottom-nav { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
